import { getReadingStorageKey, getReadingStorageUser, normalizeReadingUid } from "./readingStorageScope";

const STORAGE_KEY = "bible_reader_verse_focus";

const storagePool = () => {
  const stores = [];
  if (typeof sessionStorage !== "undefined") stores.push(sessionStorage);
  if (typeof localStorage !== "undefined") stores.push(localStorage);
  return stores;
};

const resolveFocusScope = (uid) =>
  uid !== undefined ? normalizeReadingUid(uid) : getReadingStorageUser();

const focusStorageKey = (uid) => getReadingStorageKey(STORAGE_KEY, uid);

export const toPositiveVerse = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const resolvePreferredVerse = ({
  selectedVerse,
  currentVerse,
  routeVerse,
} = {}) =>
  toPositiveVerse(selectedVerse) ||
  toPositiveVerse(routeVerse) ||
  toPositiveVerse(currentVerse) ||
  null;

const publicFocus = (focus) => {
  if (!focus) return null;
  return {
    chapterId: focus.chapterId,
    selectedVerse: focus.selectedVerse,
    currentVerse: focus.currentVerse,
    routeVerse: focus.routeVerse,
  };
};

const normalizeFocus = (parsed) => {
  if (!parsed?.chapterId) return null;
  return {
    chapterId: parsed.chapterId,
    selectedVerse: toPositiveVerse(parsed.selectedVerse),
    currentVerse: toPositiveVerse(parsed.currentVerse),
    routeVerse: toPositiveVerse(parsed.routeVerse),
    uid: normalizeReadingUid(parsed.uid),
  };
};

const readFocusFrom = (store, key) => {
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    return normalizeFocus(JSON.parse(raw));
  } catch (e) {
    return null;
  }
};

export const getReaderVerseFocus = (chapterId, uid) => {
  const stores = storagePool();
  if (!stores.length) return null;
  const scope = resolveFocusScope(uid);
  const key = focusStorageKey(scope);
  const wanted = chapterId ? String(chapterId) : "";
  for (const store of stores) {
    const focus = readFocusFrom(store, key);
    if (!focus) continue;
    if ((focus.uid || "") !== (scope || "")) continue;
    if (!wanted || focus.chapterId === wanted) return publicFocus(focus);
  }
  return null;
};

const writeFocus = (next, uid) => {
  const scope = resolveFocusScope(uid);
  const payload = { ...next, uid: scope };
  const raw = JSON.stringify(payload);
  const key = focusStorageKey(scope);
  storagePool().forEach((store) => {
    try {
      store.setItem(key, raw);
    } catch (e) {
      // ignore quota / private-mode failures
    }
  });
};

export const clearReaderVerseFocus = (uid) => {
  const scope = resolveFocusScope(uid);
  const key = focusStorageKey(scope);
  storagePool().forEach((store) => {
    try {
      store.removeItem(key);
    } catch (e) {
      // ignore quota / private-mode failures
    }
  });
};

export const setReaderVerseFocus = (
  {
    chapterId,
    selectedVerse,
    currentVerse,
    routeVerse,
  } = {},
  uid
) => {
  if (!chapterId || !storagePool().length) return null;
  try {
    const prev = getReaderVerseFocus(undefined, uid);
    const sameChapter = prev?.chapterId === chapterId;
    const nextSelected =
      selectedVerse !== undefined
        ? toPositiveVerse(selectedVerse)
        : sameChapter
          ? prev.selectedVerse
          : null;
    const nextCurrent =
      currentVerse !== undefined
        ? toPositiveVerse(currentVerse)
        : sameChapter
          ? prev.currentVerse
          : null;
    const nextRoute =
      routeVerse !== undefined
        ? toPositiveVerse(routeVerse)
        : sameChapter
          ? prev.routeVerse
          : null;
    const next = {
      chapterId,
      selectedVerse: nextSelected,
      currentVerse: nextCurrent,
      routeVerse: nextRoute,
    };
    writeFocus(next, uid);
    return publicFocus(next);
  } catch (e) {
    return null;
  }
};
